/**
 * KaTeX for every prompt, option label, truth and explanation line
 * (constitution: KaTeX with mhchem for display; input is our own).
 *
 * The words arrive rendered by the server (`engine/words.py`): text with
 * `$…$` math spans, numbers already through `engine.fmt`, which writes the
 * decimal comma in math as `{,}` so KaTeX sets `2{,}5` with no space after the
 * comma. A real newline (or the words' `\n` in text mode) is a line break.
 * mhchem is loaded once, here, so `\ce{…}` renders wherever a prompt uses it.
 *
 * `throwOnError: false`: a formula KaTeX cannot parse is shown as its source in
 * the error colour rather than taking the screen down. The content lint is
 * what keeps that from reaching her.
 */
import katex from "katex";
import "katex/contrib/mhchem";
import "katex/dist/katex.min.css";
import { Fragment, type ReactNode } from "react";

export type Span = { math: false; text: string } | { math: true; tex: string };

/** Text and math spans. An unclosed `$` leaves the rest as text. */
export function spans(s: string): Span[] {
  const out: Span[] = [];
  let buf = "";
  let inMath = false;
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (c === "$") {
      if (inMath) {
        out.push({ math: true, tex: buf });
      } else if (buf) {
        out.push({ math: false, text: buf });
      }
      buf = "";
      inMath = !inMath;
    } else {
      buf += c;
    }
  }
  if (inMath) out.push({ math: false, text: "$" + buf });
  else if (buf) out.push({ math: false, text: buf });
  return out;
}

export function texHtml(tex: string, display = false): string {
  return katex.renderToString(tex, { throwOnError: false, displayMode: display, output: "htmlAndMathml", strict: "ignore" });
}

/** A line of words with math, as the child sees it. */
export function Rich({ text, className }: { text: string; className?: string }) {
  const parts: ReactNode[] = [];
  spans(text).forEach((sp, i) => {
    if (sp.math) {
      parts.push(<span key={i} className="tex" dangerouslySetInnerHTML={{ __html: texHtml(sp.tex) }} />);
    } else {
      const lines = sp.text.split("\n");
      parts.push(
        <Fragment key={i}>
          {lines.map((ln, j) => (
            <Fragment key={j}>
              {j > 0 && <br />}
              {ln}
            </Fragment>
          ))}
        </Fragment>,
      );
    }
  });
  return <span className={className}>{parts}</span>;
}

/** Plain TeX (a truth's `tex`), which is math without the dollars. */
export function Tex({ tex }: { tex: string }) {
  return <span className="tex" dangerouslySetInnerHTML={{ __html: texHtml(tex) }} />;
}
